import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { MultiImageUploader } from "@/components/cms/MultiImageUploader";

interface PageDef {
  key: string;
  label: string;
  route: string;
}

const PAGES: PageDef[] = [
  { key: "cerimonia", label: "Scarpe da cerimonia", route: "/scarpe-da-cerimonia" },
  { key: "aziende", label: "Aziende / Bulk", route: "/aziende" },
];

const DEFAULTS: Record<string, any> = {
  cerimonia: { eyebrow: "Occasioni speciali", title: "Scarpe da cerimonia", description: "", button_text: "Scrivimi su WhatsApp", button_url: "", whatsapp_message: "Ciao! Vorrei informazioni sulle scarpe da cerimonia personalizzate.", images: [], published: true },
  aziende: { eyebrow: "Per le aziende", title: "Aziende / Bulk", description: "", button_text: "Scrivimi su WhatsApp", button_url: "", whatsapp_message: "Ciao! Vorrei informazioni sulle personalizzazioni per aziende / ordini multipli.", images: [], published: true },
};

const inputCls = "w-full border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary transition-colors";
const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">{children}</label>
);

export default function ContentPagesEditor() {
  const [rows, setRows] = useState<Record<string, any>>({});
  const [forms, setForms] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get<any[]>("/admin/content-pages");
        const byKey: Record<string, any> = {};
        (data || []).forEach((r) => (byKey[r.key] = r));
        const f: Record<string, any> = {};
        PAGES.forEach((p) => {
          const merged = { ...DEFAULTS[p.key] };
          const ex = byKey[p.key];
          if (ex) {
            ["eyebrow", "title", "description", "button_text", "button_url", "whatsapp_message"].forEach((k) => {
              if (ex[k] != null) merged[k] = ex[k];
            });
            merged.images = Array.isArray(ex.images) ? ex.images : [];
            merged.published = ex.published !== false;
          }
          f[p.key] = merged;
        });
        setRows(byKey);
        setForms(f);
      } catch (err: any) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (key: string, name: string, value: any) =>
    setForms((p) => ({ ...p, [key]: { ...p[key], [name]: value } }));

  const save = async (p: PageDef) => {
    setSaving(p.key);
    try {
      const v = forms[p.key];
      const payload = {
        key: p.key,
        eyebrow: v.eyebrow || null,
        title: v.title || null,
        description: v.description || null,
        button_text: v.button_text || null,
        button_url: v.button_url || null,
        whatsapp_message: v.whatsapp_message || null,
        images: v.images || [],
        published: v.published !== false,
        sort_order: PAGES.findIndex((x) => x.key === p.key),
      };
      const ex = rows[p.key];
      const res = ex
        ? await api.put(`/admin/content-pages/${ex.id}`, payload)
        : await api.post("/admin/content-pages", payload);
      setRows((prev) => ({ ...prev, [p.key]: res.data }));
      toast.success(`Pagina "${p.label}" salvata`);
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading)
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div data-testid="content-pages-editor">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-2">CMS</p>
        <h1 className="font-display font-black tracking-tighter text-4xl">Pagine</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-xl">
          Gestisci le pagine "Scarpe da cerimonia" e "Aziende / Bulk". Le immagini sono opzionali: se non ne carichi,
          in pagina non compare alcuno spazio vuoto. Disattivando una pagina, la sua voce sparisce dal menu.
        </p>
      </div>

      <div className="space-y-6">
        {PAGES.map((p) => {
          const v = forms[p.key] || {};
          return (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-white/10 bg-card p-6 md:p-8"
              data-testid={`page-section-${p.key}`}
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="font-display font-bold text-xl">{p.label}</h2>
                    <a href={p.route} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-secondary" title="Apri pagina">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 font-mono">{p.route}</p>
                </div>
                <button
                  data-testid={`page-save-${p.key}`}
                  onClick={() => save(p)}
                  disabled={saving === p.key}
                  className="shrink-0 flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-xs px-5 py-3 hover:-translate-y-[2px] transition-transform disabled:opacity-60"
                >
                  {saving === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
                </button>
              </div>

              <label className="flex items-center gap-3 mb-5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid={`page-published-${p.key}`}
                  checked={v.published !== false}
                  onChange={(e) => setField(p.key, "published", e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-sm">Pagina attiva (visibile nel menu e raggiungibile)</span>
              </label>

              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <Label>Sopra-titolo</Label>
                  <input data-testid={`page-field-${p.key}-eyebrow`} value={v.eyebrow ?? ""} onChange={(e) => setField(p.key, "eyebrow", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <Label>Titolo</Label>
                  <input data-testid={`page-field-${p.key}-title`} value={v.title ?? ""} onChange={(e) => setField(p.key, "title", e.target.value)} className={inputCls} />
                </div>
                <div className="md:col-span-2">
                  <Label>Testo descrittivo</Label>
                  <textarea data-testid={`page-field-${p.key}-description`} value={v.description ?? ""} onChange={(e) => setField(p.key, "description", e.target.value)} rows={5} className={inputCls} />
                </div>
                <div>
                  <Label>Testo pulsante</Label>
                  <input data-testid={`page-field-${p.key}-button_text`} value={v.button_text ?? ""} onChange={(e) => setField(p.key, "button_text", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <Label>Link pulsante (WhatsApp)</Label>
                  <input data-testid={`page-field-${p.key}-button_url`} value={v.button_url ?? ""} placeholder="https://wa.me/39..." onChange={(e) => setField(p.key, "button_url", e.target.value)} className={inputCls} />
                </div>
                <div className="md:col-span-2">
                  <Label>Messaggio WhatsApp precompilato (opzionale)</Label>
                  <textarea data-testid={`page-field-${p.key}-whatsapp_message`} value={v.whatsapp_message ?? ""} placeholder="Ciao! Vorrei info su..." onChange={(e) => setField(p.key, "whatsapp_message", e.target.value)} rows={2} className={inputCls} />
                  <p className="text-[11px] text-muted-foreground mt-1">Usato solo se il link è WhatsApp (wa.me): apre la chat con questo testo già scritto.</p>
                </div>
                <div className="md:col-span-2">
                  <Label>Immagini (opzionali)</Label>
                  <MultiImageUploader value={v.images} onChange={(imgs) => setField(p.key, "images", imgs)} testid={`page-images-${p.key}`} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
